import { fetchWalletBalance, fetchWalletHistory, fetchWalletRates } from '@/modules/wallet/api/walletApi'
import type {
  WalletApiWallet,
  WalletAsset,
  WalletDashboardData,
  WalletHistoryRequest,
  WalletHistoryState,
  WalletRateMap,
  WalletTransaction,
  WalletTransactionApiItem,
  WalletTransactionStatus,
} from '@/modules/wallet/types/wallet'

const HISTORY_PAGE_SIZE = 50
const DEFAULT_BASE_CURRENCY = 'USD'
// Approximate HMC rate until exchange listing ($0.10 per HMC)
const HMC_APPROXIMATE_RATE = 0.10

const currencyLabels: Record<string, string> = {
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  USDT: 'Tether',
  USDC: 'USD Coin',
  BNB: 'Binance Coin',
  SOL: 'Solana',
  TON: 'Toncoin',
  HMC: 'GonkaGame Coin',
  GNK: 'Gonka Token',
}

const transactionStatusMap: Record<number, WalletTransactionStatus> = {
  [-1]: 'failed',
  0: 'pending',
  1: 'success',
  2: 'processing',
}

const normalizeAmount = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

interface UsdConversionResult {
  value: number
  isApproximate: boolean
}

const toUsd = (currency: string, amount: number, rates: WalletRateMap): UsdConversionResult | undefined => {
  // Normalize currency to uppercase for lookup
  const normalizedCurrency = currency.toUpperCase()

  // Check if this is HMC - use approximate rate
  if (normalizedCurrency === 'HMC') {
    const usd = amount * HMC_APPROXIMATE_RATE
    if (!Number.isFinite(usd)) {
      return undefined
    }
    return {
      value: usd >= 1 ? Number(usd.toFixed(2)) : Number(usd.toFixed(4)),
      isApproximate: true,
    }
  }

  // Try direct lookup, uppercase, or lowercase
  const rate = rates[currency]
    ?? rates[normalizedCurrency]
    ?? rates[currency.toLowerCase()]
    ?? (normalizedCurrency === 'USD' || normalizedCurrency === 'USDT' || normalizedCurrency === DEFAULT_BASE_CURRENCY ? 1 : undefined)

  if (!rate) {
    return undefined
  }

  const usd = amount * rate
  if (!Number.isFinite(usd)) {
    return undefined
  }

  return {
    value: usd >= 1 ? Number(usd.toFixed(2)) : Number(usd.toFixed(4)),
    isApproximate: false,
  }
}

const formatFraction = (amount: number, minFraction = 2, maxFraction = 8): string => {
  const fixed = amount.toFixed(maxFraction)
  const [integerPart, fractionPart = ''] = fixed.split('.')

  if (!fractionPart) {
    return integerPart
  }

  let trimmed = fractionPart
  while (trimmed.length > minFraction && trimmed.endsWith('0')) {
    trimmed = trimmed.slice(0, -1)
  }

  if (!trimmed.length) {
    return `${integerPart}.${'0'.repeat(minFraction)}`
  }

  return `${integerPart}.${trimmed}`
}

const formatAmount = (amount: number, currency: string, formatted?: string) => {
  if (formatted) {
    return formatted
  }

  const precision = amount > 1 ? 2 : 8
  const number = formatFraction(amount, 2, precision)
  return `${number} ${currency}`
}

const normalizeAsset = (wallet: WalletApiWallet, rates: WalletRateMap): WalletAsset => {
  const amount = normalizeAmount(wallet.amount)
  const label = currencyLabels[wallet.currency] ?? wallet.currency
  const usdConversion = toUsd(wallet.currency, amount, rates)

  return {
    id: wallet.id,
    currency: wallet.currency,
    label,
    amount,
    amountFormatted: formatAmount(amount, wallet.currency, wallet.amount_string),
    usdValue: usdConversion?.value,
    isApproximate: usdConversion?.isApproximate,
    trend: 'flat',
    type: wallet.type,
  }
}

const normalizeDate = (value?: string | number) => {
  if (!value) {
    return new Date().toISOString()
  }

  if (typeof value === 'number') {
    return new Date(value * 1000).toISOString()
  }

  const normalized = value.replace(' ', 'T')
  // Wallet API returns UTC SQL datetimes without an offset. Preserve explicit
  // offsets; Intl.DateTimeFormat in both history views uses the browser's zone.
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
  const date = new Date(hasTimezone ? normalized : `${normalized}Z`)

  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString()
  }

  return date.toISOString()
}

export const normalizeTransaction = (item: WalletTransactionApiItem): WalletTransaction => {
  const amount = normalizeAmount(item.amount)

  return {
    id: item.id,
    walletId: item.wallet_id,
    transactionId: item.transaction_id,
    amount,
    amountFormatted: formatAmount(amount, item.currency, item.amount_string),
    currency: item.currency,
    date: normalizeDate(item.datetime),
    description: item.description,
    status: transactionStatusMap[item.status] ?? 'pending',
    usdValue: undefined,
    txHash: item.tx_hash,
    type: item.type,
  }
}

const buildOverview = (assets: WalletAsset[]): WalletDashboardData['overview'] => {
  const totalUsd = assets.reduce((acc, asset) => acc + (asset.usdValue ?? 0), 0)
  const baseCurrency = assets[0]?.currency ?? DEFAULT_BASE_CURRENCY
  const rewardsSample = assets[0]?.usdValue ?? 0
  const rewardsToday = rewardsSample ? Number((rewardsSample * 0.003).toFixed(4)) : undefined
  const nativeTotal = assets
    .filter((asset) => asset.currency === baseCurrency)
    .reduce((acc, asset) => acc + asset.amount, 0)

  const hasUsd = totalUsd > 0
  const totalLabel = hasUsd
    ? `$${totalUsd.toFixed(2)}`
    : nativeTotal > 0
      ? formatAmount(nativeTotal, baseCurrency)
      : assets[0]?.amountFormatted ?? '$0.00'

  return {
    totalUsd: hasUsd ? Number(totalUsd.toFixed(2)) : 0,
    totalLabel,
    rewardsToday,
    changePercent: 0.0,
    baseCurrency,
  }
}

const normalizeRates = (ratesData: any): WalletRateMap => {
  const normalized: WalletRateMap = {}
  if (!ratesData || typeof ratesData !== 'object') return normalized

  const rates = ratesData.rates || ratesData
  const pairs = ratesData.pairs || {}

  // First, try to get rates from pairs (XXX/USD format) - most accurate
  for (const [pairKey, pairValue] of Object.entries(pairs)) {
    if (pairKey.endsWith('/USD') && typeof pairValue === 'object' && pairValue !== null) {
      const currency = pairKey.replace('/USD', '')
      const rate = (pairValue as any).rate
      if (typeof rate === 'number' && Number.isFinite(rate)) {
        normalized[currency] = rate
        normalized[currency.toUpperCase()] = rate
      }
    }
  }

  // Get USD to base rate for fallback calculation
  const usdToBase = rates?.USD?.to_base ?? rates?.usd?.to_base ?? 1

  // Fallback: calculate from to_base rates if pairs not available
  for (const [key, value] of Object.entries(rates)) {
    // Skip if already have this currency from pairs
    if (normalized[key] || normalized[key.toUpperCase()]) continue

    let toBaseRate: number | undefined

    if (typeof value === 'number') {
      toBaseRate = value
    } else if (typeof value === 'string') {
      toBaseRate = parseFloat(value)
    } else if (typeof value === 'object' && value !== null) {
      const raw = (value as any).to_base
      toBaseRate = typeof raw === 'string' ? parseFloat(raw) : raw
    }

    // Convert to_base (rate to RUB) to rate to USD: rate_to_usd = to_base / usd_to_base
    if (toBaseRate !== undefined && Number.isFinite(toBaseRate) && usdToBase > 0) {
      const toUsdRate = toBaseRate / usdToBase
      if (Number.isFinite(toUsdRate)) {
        normalized[key] = toUsdRate
        normalized[key.toUpperCase()] = toUsdRate
      }
    }
  }

  // Ensure base currencies are present
  if (!normalized['USD']) normalized['USD'] = 1
  if (!normalized['USDT']) normalized['USDT'] = 1

  return normalized
}

export const loadWalletDashboard = async (): Promise<WalletDashboardData> => {
  // Load balance and history immediately, don't wait for rates
  const [balanceResponse, historyResponse] = await Promise.all([
    fetchWalletBalance(),
    fetchWalletHistory({ page: 1 }),
  ])

  // Assets without USD conversion initially
  const assets = balanceResponse.wallets.map((wallet) => normalizeAsset(wallet, {}))
  const overview = buildOverview(assets)
  const recentTransactions = (historyResponse.items ?? [])
    .slice(0, 5)
    .map((item) => normalizeTransaction(item))

  return {
    overview,
    assets,
    recentTransactions,
    minWithdrawal: balanceResponse.min_withdrawal ?? {},
    feePercent: balanceResponse.fee_percent ?? {},
  }
}

/**
 * Load exchange rates separately - this can be called after initial dashboard load
 */
export const loadExchangeRates = async (): Promise<WalletRateMap> => {
  const ratesResponse = await fetchWalletRates().catch(() => ({ rates: {} }))
  return normalizeRates(ratesResponse?.rates)
}

/**
 * Update assets with USD conversion values based on loaded rates
 */
export const updateAssetsWithRates = (assets: WalletAsset[], rates: WalletRateMap): WalletAsset[] => {
  return assets.map(asset => {
    const usdConversion = toUsd(asset.currency, asset.amount, rates)
    return {
      ...asset,
      usdValue: usdConversion?.value,
      isApproximate: usdConversion?.isApproximate,
    }
  })
}

export const loadWalletHistory = async (params: WalletHistoryRequest = {}): Promise<WalletHistoryState> => {
  const historyResponse = await fetchWalletHistory(params)

  const page = params.page ?? 1
  const items = (historyResponse.items ?? []).map((item) => normalizeTransaction(item))
  const hasMore = items.length === HISTORY_PAGE_SIZE

  return {
    items,
    page,
    hasMore,
  }
}
